// AddressReviewModal.jsx
import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import api from "../../utils/api";
import { toast } from "react-toastify";
import "./AddressReviewModal.scss";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  bgcolor: "background.paper",
  p: 4,
  maxHeight: "90vh",
  overflow: "auto",
};

const AddressField = ({ label, value, isChanged = false }) => (
  <div className={`address-field ${isChanged ? "changed" : ""}`}>
    <Typography variant="subtitle2" component="span" className="field-label">
      {label}:
    </Typography>
    <Typography variant="body2" component="span" className="field-value">
      {value || "—"}
    </Typography>
  </div>
);

const AddressReviewModal = ({ open, handleClose, requestData }) => {
  const [processing, setProcessing] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  if (!requestData) return null;

  const currentAddress = requestData.subscription?.address;
  const newAddress = requestData.address;

  const handleAction = async (status) => {
    setProcessing(true);
    setSelectedAction(status ? "approve" : "reject");
    try {
      await api.patch(
        `/admin/subscriptions/changeAddress-requests/${requestData._id}/approve`,
        { status }
      );
      toast.success(
        status ? "تم الموافقة على الطلب بنجاح" : "تم رفض الطلب بنجاح"
      );
      // Close modal and refresh data after successful action
      handleClose(true);
    } catch (error) {
      console.error("Error processing address change request:", error);
      toast.error("حدث خطأ أثناء معالجة الطلب");
      setProcessing(false);
      setSelectedAction(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !processing && handleClose()}
      aria-labelledby="address-review-modal-title"
    >
      <Box sx={style} className="address-review-modal">
        <Typography
          id="address-review-modal-title"
          variant="h6"
          component="h2"
          align="center"
          sx={{ mb: 3 }}
        >
          مراجعة طلب تغيير العنوان
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            معلومات المستخدم
          </Typography>
          <Paper elevation={1} sx={{ p: 2 }}>
            <AddressField label="الاسم" value={requestData.user?.name} />
            <AddressField
              label="رقم الهاتف"
              value={requestData.user?.phoneNumber}
            />
            <AddressField
              label="اسم الباقة"
              value={
                requestData.subscription?.package?.name?.ar ||
                requestData.subscription?.package?.name?.en
              }
            />
            <AddressField
              label="سعر الباقة"
              value={`${requestData.subscription?.package?.price} ريال`}
            />
          </Paper>
        </Box>

        <Grid container spacing={2}>
          {/* Current Address */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1 }}
              className="current-address-title"
            >
              العنوان الحالي
            </Typography>
            <Paper
              elevation={1}
              sx={{ p: 2, height: "100%" }}
              className="current-address"
            >
              <AddressField label="الاسم" value={currentAddress?.name} />
              <AddressField label="المدينة" value={currentAddress?.city} />
              <AddressField label="المنطقة" value={currentAddress?.region} />
              <AddressField label="الشارع" value={currentAddress?.street} />
              <AddressField
                label="رقم المبنى"
                value={currentAddress?.buildingNo}
              />
              <AddressField
                label="رقم الطابق"
                value={currentAddress?.floorNo}
              />
              <AddressField
                label="علامة مميزة"
                value={currentAddress?.addressSign}
              />
              <AddressField
                label="الإحداثيات"
                value={currentAddress?.coordinates?.coordinates?.join(", ")}
              />
            </Paper>
          </Grid>

          {/* New Address */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1 }}
              className="new-address-title"
            >
              العنوان الجديد
            </Typography>
            <Paper
              elevation={1}
              sx={{ p: 2, height: "100%" }}
              className="new-address"
            >
              <AddressField
                label="الاسم"
                value={newAddress?.name}
                isChanged={currentAddress?.name !== newAddress?.name}
              />
              <AddressField
                label="المدينة"
                value={newAddress?.city}
                isChanged={currentAddress?.city !== newAddress?.city}
              />
              <AddressField
                label="المنطقة"
                value={newAddress?.region}
                isChanged={currentAddress?.region !== newAddress?.region}
              />
              <AddressField
                label="الشارع"
                value={newAddress?.street}
                isChanged={currentAddress?.street !== newAddress?.street}
              />
              <AddressField
                label="رقم المبنى"
                value={newAddress?.buildingNo}
                isChanged={
                  currentAddress?.buildingNo !== newAddress?.buildingNo
                }
              />
              <AddressField
                label="رقم الطابق"
                value={newAddress?.floorNo}
                isChanged={currentAddress?.floorNo !== newAddress?.floorNo}
              />
              <AddressField
                label="علامة مميزة"
                value={newAddress?.addressSign}
                isChanged={
                  currentAddress?.addressSign !== newAddress?.addressSign
                }
              />
              <AddressField
                label="الإحداثيات"
                value={newAddress?.coordinates?.coordinates?.join(", ")}
                isChanged={
                  currentAddress?.coordinates?.coordinates?.[0] !==
                    newAddress?.coordinates?.coordinates?.[0] ||
                  currentAddress?.coordinates?.coordinates?.[1] !==
                    newAddress?.coordinates?.coordinates?.[1]
                }
              />
            </Paper>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 4,
            position: "relative",
            padding: "20px 0",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: "25%",
              width: "50%",
              height: "1px",
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => handleAction(true)}
            disabled={processing}
            sx={{
              fontSize: "1rem",
              py: 1,
              px: 4,
            }}
          >
            {processing && selectedAction === "approve" ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "الموافقة على الطلب"
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => handleAction(false)}
            disabled={processing}
            sx={{
              fontSize: "1rem",
              py: 1,
              px: 4,
            }}
          >
            {processing && selectedAction === "reject" ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "رفض الطلب"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddressReviewModal;
